import type { components } from '@/api/schema'
import type { EmailEvent } from '@/api'
import { EventStatus } from '@/lib/enum-display'

export type ClassificationRun = components['schemas']['ClassificationHistory']

export const classificationReasons: Record<
  NonNullable<ClassificationRun['reason']>,
  string
> = {
  payload_too_large: 'This email and your instructions are too long to process together.',
  setup_required: 'Finish this inbox’s instructions and actions, then turn processing on.',
  paused: 'Processing was paused before everything finished.',
  cancel_requested: 'Processing was stopped before everything finished.',
  deleted: 'The inbox was disconnected.',
  ownership_changed: 'Access to this inbox changed. Reconnect it to continue.',
  config_changed: 'Skipped because your settings changed while this email was being handled.',
  not_configured: 'Save this inbox’s instructions and actions first.',
  missing_scopes: 'Reconnect this inbox to allow the actions you turned on.',
  target_missing: 'A label or folder it needed no longer exists. Choose another under Actions.',
  message_missing: 'This email was deleted or moved before anything could be done.',
  gate_unavailable: 'Automatic actions aren’t available right now. They’ll resume on their own.',
  ineligible: 'Nothing was done because the answer wasn’t confident enough.',
  duplicate: 'Already handled. See the other event for this email.',
  provider_unavailable: 'Your email provider didn’t respond, so some actions didn’t finish.',
  reconnect_required: 'Reconnect this inbox to continue.',
  unknown_outcome: 'We couldn’t confirm an action finished. Check this email in your inbox.',
  invalid_result: 'We couldn’t work out what to do with this email.',
  quota_exceeded: 'You’ve reached your plan’s email limit.',
  legacy_work_running: 'Earlier processing has to finish first.',
  mailbox_already_controlled: 'This inbox is already connected under another account.',
  lease_lost: 'This email was picked up again by another attempt.',
  context_exceeded: 'This email is too long to read, so it was left alone.',
  missing_credentials: 'Automatic sorting isn’t available right now. Try again later.',
  price_unavailable: 'Automatic sorting isn’t available right now. Try again later.',
  rate_limited: 'Your email provider asked us to slow down. We’ll retry shortly.',
  subscription_limit: 'You’ve reached your plan’s limit. Check your plan to continue.',
  unsupported_result: 'We couldn’t use this answer to act on the email.',
}

type Action = ClassificationRun['actions'][number]

const list = (names: string[]) =>
  new Intl.ListFormat('en', { type: 'conjunction' }).format(names)
const sentence = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

// An action not taken only because the model was under the 70% bar.
const heldBack = (action: Action) =>
  action.status === 'skipped' && action.reason === 'ineligible'

function done(action: Action) {
  const target = action.target_name
  switch (action.kind) {
    case 'mark_read':
      return 'marked as read'
    case 'archive':
      return 'archived'
    case 'folder':
      return target ? `moved to ${target}` : 'moved'
    case 'label':
      return target ? `labeled ${target}` : 'labeled'
    case 'category':
      return target ? `categorized ${target}` : 'categorized'
  }
}

function notDone(action: Action) {
  const target = action.target_name
  return {
    mark_read: 'mark as read',
    archive: 'archive',
    folder: target ? `move to ${target}` : 'move',
    label: target ? `label ${target}` : 'label',
    category: target ? `category ${target}` : 'category',
  }[action.kind]
}

const sure = (action: Action) =>
  action.decision_probability == null
    ? ''
    : ` (${Math.round(action.decision_probability * 100)}% sure)`

/** "Archive held back (61% sure)" when some actions ran and others fell under
 * the confidence bar; null otherwise. */
export function classificationHeldBack(run: ClassificationRun) {
  const held = run.actions.filter(heldBack)
  if (!held.length || !run.actions.some((a) => a.status === 'succeeded')) return null
  if (held.length === 1) return `${sentence(notDone(held[0]!))} held back${sure(held[0]!)}`
  return `${sentence(list(held.map((a) => notDone(a) + sure(a))))} held back`
}

export function classificationEventTitle(run: ClassificationRun) {
  const completed = run.actions.filter(
    (action) => action.status === 'succeeded',
  )
  if (completed.length) return sentence(list(completed.map(done)))
  if (run.reason === 'duplicate') return 'Already handled'
  if (run.reason === 'setup_required') return 'Inbox needs setup'
  if (run.reason === 'message_missing') return 'Email was deleted before it was handled'
  if (
    run.actions.some((action) => action.status === 'unknown') ||
    run.status === 'unknown'
  )
    return 'Couldn’t confirm what happened'
  if (
    run.status === 'failed' ||
    run.actions.some((action) => action.status === 'failed')
  )
    return 'Couldn’t finish'
  if (run.actions.some((action) => action.status === 'started'))
    return 'Working on it'
  if (run.actions.some((action) => action.status === 'pending'))
    return 'Waiting to act'
  if (run.status === 'deferred') return 'Will retry shortly'
  return 'Left as is'
}

export type EventTone = 'done' | 'attention' | 'error' | 'working'

// One visual state per event, shared by Events, Dashboard and event detail.
export function classificationEventTone(run: ClassificationRun): EventTone {
  // Runs saved before held-back actions counted as finished are "partial".
  const partlyHeldBack =
    run.status === 'partial' &&
    run.actions.every((action) => action.status === 'succeeded' || heldBack(action))
  if (
    run.status === 'failed' ||
    run.status === 'unknown' ||
    (run.status === 'partial' && !partlyHeldBack) ||
    run.actions.some(
      (action) => action.status === 'failed' || action.status === 'unknown',
    )
  )
    return 'error'
  if (
    run.status === 'classified' ||
    run.status === 'deferred' ||
    run.actions.some(
      (action) => action.status === 'pending' || action.status === 'started',
    )
  )
    return 'working'
  if (run.status === 'blocked' && run.reason !== 'ineligible') return 'attention'
  return 'done'
}

export function eventTone(event: EmailEvent): EventTone {
  if (event.classification) return classificationEventTone(event.classification)
  if (event.status === EventStatus.DONE) return 'done'
  if (event.status === EventStatus.PENDING || event.status === EventStatus.ANALYZING)
    return 'working'
  return 'error'
}
